// Create / edit a staff task. The same dialog opens from the Tasks page and
// from a record page ("+ Task" on an order, product, sample or work order),
// in which case the link is fixed and shown read-only.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { DatePicker } from '../../components/ui/date-picker'
import { Field } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { Select } from '../../components/ui/select'
import { fetchMe } from '../../lib/auth'
import { parsePlainDate, type PlainDate } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import { AssigneePicker } from './AssigneePicker'
import {
  createTask,
  getProject,
  listAssignees,
  listProjects,
  StaffTaskApiError,
  staffTaskKeys,
  updateTask,
  REPEAT_RULES,
  type RepeatRule,
  type StaffTask,
  type TaskLink,
  type TaskPriority,
} from './api'

export const TEXTAREA_CLASS =
  'w-full rounded-[var(--field-radius)] border border-[var(--field-border)] bg-[var(--field-bg)] px-[var(--field-padding-x)] py-2 text-[length:var(--field-font-size)] text-[var(--field-text)] placeholder:text-[var(--field-placeholder)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:shadow-[var(--field-focus-shadow)] disabled:cursor-not-allowed disabled:bg-[var(--field-disabled-bg)]'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Editing an existing task; omitted when creating. */
  task?: StaffTask
  /** Fixed link when the dialog opens from a record page. */
  link?: TaskLink
  /** Pre-filled due date (a calendar day was clicked). */
  initialDueDate?: PlainDate
  /** Pre-filled project / stage (opened from a project page). */
  initialProjectId?: string
  initialStageId?: string | null
  onSaved?: (task: StaffTask) => void
}

export function TaskFormDialog({
  open,
  onOpenChange,
  task,
  link,
  initialDueDate,
  initialProjectId,
  initialStageId,
  onSaved,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <TaskForm
          task={task}
          link={link}
          initialDueDate={initialDueDate}
          initialProjectId={initialProjectId}
          initialStageId={initialStageId}
          onClose={() => onOpenChange(false)}
          onSaved={onSaved}
        />
      )}
    </Dialog>
  )
}

function TaskForm({
  task,
  link,
  initialDueDate,
  initialProjectId,
  initialStageId,
  onClose,
  onSaved,
}: {
  task?: StaffTask
  link?: TaskLink
  initialDueDate?: PlainDate
  initialProjectId?: string
  initialStageId?: string | null
  onClose: () => void
  onSaved?: (task: StaffTask) => void
}) {
  const t = useT(staffTasksI18n)
  const queryClient = useQueryClient()
  const [title, setTitle] = useState(task?.title ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  // null = not chosen yet: a new task defaults to the person opening it.
  const [assigneeChoice, setAssignees] = useState<string[] | null>(
    task ? task.assignees.map((person) => person.user_id) : null,
  )
  const [due, setDue] = useState<PlainDate | null>(
    task?.due_date ? parsePlainDate(task.due_date) : (initialDueDate ?? null),
  )
  const [projectId, setProjectId] = useState(task?.project?.id ?? initialProjectId ?? '')
  const [stageId, setStageId] = useState(task?.stage_id ?? initialStageId ?? '')
  const [repeat, setRepeat] = useState<RepeatRule | ''>(task?.repeat_rule ?? '')
  const [repeatUntil, setRepeatUntil] = useState<PlainDate | null>(
    task?.repeat_until ? parsePlainDate(task.repeat_until) : null,
  )
  const [priority, setPriority] = useState<TaskPriority>(task?.priority ?? 'normal')
  const [titleError, setTitleError] = useState<string | null>(null)

  const assignees = useQuery({ queryKey: staffTaskKeys.assignees, queryFn: listAssignees })
  const projects = useQuery({ queryKey: staffTaskKeys.projects, queryFn: listProjects })
  const project = useQuery({
    queryKey: staffTaskKeys.project(projectId),
    queryFn: () => getProject(projectId),
    enabled: !!projectId,
  })
  const stages = projectId ? (project.data?.stages ?? []) : []
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: fetchMe, staleTime: 5 * 60 * 1000 })
  const meId = me.data?.user.id
  // A private project's tasks only go to its members.
  const privateMembers =
    projectId && project.data?.project.is_private
      ? new Set((project.data.project.members ?? []).map((person) => person.user_id))
      : null
  const people = (assignees.data?.items ?? []).filter(
    (person) => !privateMembers || privateMembers.has(person.user_id),
  )
  const chosen = (
    assigneeChoice ?? (meId && people.some((person) => person.user_id === meId) ? [meId] : [])
  ).filter((id) => !privateMembers || privateMembers.has(id))
  const shownLink = task?.link ?? link

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        assignee_user_ids: chosen,
        due_date: due,
        priority,
        project_id: projectId || null,
        stage_id: (projectId && stageId) || null,
        repeat_rule: repeat || null,
        repeat_until: repeat ? repeatUntil : null,
      }
      return task ? updateTask(task.id, body) : createTask({ ...body, link: link ?? null })
    },
    onSuccess: (saved) => {
      toast.success(task ? t.toasts.saved : t.toasts.created)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
      onSaved?.(saved)
      onClose()
    },
    onError: (err) => {
      const code = err instanceof StaffTaskApiError ? err.code : undefined
      toast.error(
        code === 'assignee_not_in_team'
          ? t.toasts.notInTeam
          : code === 'assignee_not_in_private_project'
            ? t.privacy.notMember
            : t.toasts.error,
      )
    },
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!title.trim()) {
      setTitleError(t.form.titleRequired)
      return
    }
    save.mutate()
  }

  return (
    <DialogContent>
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="staff-task-form">
        <DialogHeader>
          <DialogTitle>{task ? t.form.editTitle : t.form.createTitle}</DialogTitle>
        </DialogHeader>

        {shownLink && (
          <p className="m-0 text-sm text-[var(--color-text-muted)]">
            {t.form.linkedTo}:{' '}
            <span className="font-medium text-[var(--color-text)]">
              {t.linked.kinds[shownLink.kind]} · {shownLink.label}
            </span>
          </p>
        )}

        <Field id="staff-task-title" label={t.form.titleLabel} required error={titleError}>
          <Input
            autoFocus
            maxLength={200}
            value={title}
            placeholder={t.form.titlePlaceholder}
            onChange={(e) => {
              setTitle(e.target.value)
              if (titleError) setTitleError(null)
            }}
          />
        </Field>

        <Field id="staff-task-description" label={t.form.descriptionLabel}>
          <textarea
            rows={4}
            maxLength={10000}
            className={TEXTAREA_CLASS}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="staff-task-assignee" label={t.structure.assignees}>
            <AssigneePicker
              id="staff-task-assignee"
              people={people}
              value={chosen}
              onChange={setAssignees}
            />
          </Field>
          <Field id="staff-task-priority" label={t.form.priorityLabel}>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
            >
              <option value="normal">{t.priority.normal}</option>
              <option value="urgent">{t.priority.urgent}</option>
            </Select>
          </Field>
        </div>

        <DatePicker label={t.form.dueLabel} value={due} onChange={setDue} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="staff-task-repeat" label={t.repeat.label}>
            <Select value={repeat} onChange={(e) => setRepeat(e.target.value as RepeatRule | '')}>
              <option value="">{t.repeat.none}</option>
              {REPEAT_RULES.map((rule) => (
                <option key={rule} value={rule}>
                  {t.repeat.rules[rule]}
                </option>
              ))}
            </Select>
          </Field>
          {repeat && (
            <DatePicker label={t.repeat.untilLabel} value={repeatUntil} onChange={setRepeatUntil} />
          )}
        </div>

        {(projects.data?.items.length ?? 0) > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="staff-task-project" label={t.projects.taskField.project}>
              <Select
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value)
                  setStageId('')
                }}
              >
                <option value="">{t.projects.taskField.none}</option>
                {(projects.data?.items ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>
            {stages.length > 0 && (
              <Field id="staff-task-stage" label={t.projects.taskField.stage}>
                <Select value={stageId} onChange={(e) => setStageId(e.target.value)}>
                  <option value="">{t.projects.detail.noStage}</option>
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t.form.cancel}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {task ? t.form.save : t.form.create}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
